import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  IconButton,
  Alert,
  Slider,
  FormHelperText,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import questionService, { Question, UpdateQuestionData } from '../../services/questionService';
import ContentRenderer from '../common/ContentRenderer';

interface QuestionEditFormProps {
  open: boolean;
  question: Question | null;
  onClose: () => void;
  onSuccess: () => void;
}

const QuestionEditForm: React.FC<QuestionEditFormProps> = ({
  open,
  question,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<UpdateQuestionData>({
    text: '',
    options: ['', ''],
    correct_answer: undefined,
    correct_answer_id: '',
    explanation: '',
    type: 'multiple_choice',
    difficulty: 5,
    tags: []
  });
  const [correctAnswerId, setCorrectAnswerId] = useState<string>('');
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (question) {
      console.log('Question data received:', question);
      
      // Handle correct_answer - ensure it's always a number
      let correctAnswer = question.correct_answer;
      if (typeof question.correct_answer === 'string') {
        correctAnswer = parseInt(question.correct_answer) || 1;
      }
      
      // Use correct_answer_id directly if available, otherwise generate from correct_answer
      let correctAnswerId = question.correct_answer_id;
      if (!correctAnswerId && correctAnswer) {
        correctAnswerId = `option_${correctAnswer}`;
      }
      
      console.log('Processed data:', {
        correctAnswer,
        correctAnswerId,
        options: question.options
      });
      
      setFormData({
        text: question.text,
        options: question.options || ['', ''],
        correct_answer: correctAnswer,
        correct_answer_id: correctAnswerId || '',
        explanation: question.explanation || '',
        type: question.type,
        difficulty: question.difficulty || 5,
        tags: question.tags || []
      });
      
      // Set correct answer ID separately
      setCorrectAnswerId(correctAnswerId || '');
    }
  }, [question]);

  const handleInputChange = (field: keyof UpdateQuestionData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...(formData.options || [])];
    newOptions[index] = value;
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const addOption = () => {
    if ((formData.options?.length || 0) < 6) {
      setFormData(prev => ({
        ...prev,
        options: [...(prev.options || []), '']
      }));
    }
  };

  const removeOption = (index: number) => {
    if ((formData.options?.length || 0) > 2) {
      const newOptions = formData.options?.filter((_, i) => i !== index) || [];
      setFormData(prev => ({
        ...prev,
        options: newOptions
      }));
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question) return;

    setLoading(true);
    setError(null);

    try {
      // Validate form
      if (!formData.text?.trim()) {
        throw new Error('Nội dung câu hỏi là bắt buộc');
      }

      if (formData.type === 'multiple_choice' && (!formData.options || formData.options.length < 2)) {
        throw new Error('Câu hỏi trắc nghiệm cần ít nhất 2 lựa chọn');
      }

      if (!formData.correct_answer_id) {
        throw new Error('Đáp án đúng là bắt buộc');
      }

      // Convert options to answer objects
      const answerObjects = formData.options?.map((option, index) => ({
        id: `option_${index + 1}`,
        text: option
      })) || [];

      // Extract correct answer index from correct_answer_id
      const correctAnswerIndex = parseInt(formData.correct_answer_id.replace('option_', ''));

      const updateData: UpdateQuestionData = {
        text: formData.text,
        options: formData.options,
        answer_objects: answerObjects,
        correct_answer: correctAnswerIndex,
        correct_answer_id: formData.correct_answer_id,
        explanation: formData.explanation,
        type: formData.type,
        difficulty: formData.difficulty,
        tags: formData.tags
      };

      await questionService.updateQuestion(question.id, updateData);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Không thể cập nhật câu hỏi');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Chỉnh sửa câu hỏi
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label="Chỉnh sửa" />
              <Tab label="Xem trước" />
            </Tabs>
          </Box>

          {/* Edit Tab */}
          {activeTab === 0 && (
            <>
              {/* Question Text */}
              <TextField
                fullWidth
                label="Nội dung câu hỏi"
                multiline
                rows={3}
                value={formData.text}
                onChange={(e) => handleInputChange('text', e.target.value)}
                required
                sx={{ mb: 2 }}
                helperText="Sử dụng cú pháp LaTeX cho toán học: $x^2$ cho dạng nội tuyến, $$x^2$$ cho dạng khối"
              />

              {/* Question Type */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Loại câu hỏi</InputLabel>
                <Select
                  value={formData.type}
                  label="Loại câu hỏi"
                  onChange={(e) => handleInputChange('type', e.target.value)}
                >
                  <MenuItem value="multiple_choice">Trắc nghiệm</MenuItem>
                  <MenuItem value="true_false">Đúng/Sai</MenuItem>
                  <MenuItem value="short_answer">Câu trả lời ngắn</MenuItem>
                  <MenuItem value="fill_in_blank">Điền vào chỗ trống</MenuItem>
                </Select>
              </FormControl>

              {/* Options (for multiple choice) */}
              {formData.type === 'multiple_choice' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Các lựa chọn
                  </Typography>
                  {formData.options?.map((option, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                      <TextField
                        fullWidth
                        label={`Lựa chọn ${index + 1}`}
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        required
                      />
                      {(formData.options?.length || 0) > 2 && (
                        <IconButton onClick={() => removeOption(index)} color="error">
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {(formData.options?.length || 0) < 6 && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addOption}
                      size="small"
                      sx={{ mt: 1 }}
                    >
                      Thêm lựa chọn
                    </Button>
                  )}
                </Box>
              )}

              {/* Correct Answer */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Đáp án đúng</InputLabel>
                <Select
                  value={correctAnswerId}
                  onChange={(e) => {
                    setCorrectAnswerId(e.target.value);
                    handleInputChange('correct_answer_id', e.target.value);
                  }}
                  label="Đáp án đúng"
                  required
                >
                  {formData.options?.map((option, index) => {
                    const optionValue = `option_${index + 1}`;
                    console.log(`Option ${index + 1}:`, { option, optionValue, isSelected: optionValue === correctAnswerId });
                    return (
                      <MenuItem key={index} value={optionValue}>
                        {String.fromCharCode(65 + index)}. {option}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>
                  {formData.type === 'multiple_choice' 
                    ? 'Chọn đáp án đúng từ danh sách trên'
                    : 'Chọn đáp án đúng'
                  }
                </FormHelperText>
              </FormControl>

              {/* Explanation */}
              <TextField
                fullWidth
                label="Giải thích (Tùy chọn)"
                multiline
                rows={2}
                value={formData.explanation}
                onChange={(e) => handleInputChange('explanation', e.target.value)}
                sx={{ mb: 2 }}
              />

              {/* Difficulty */}
              <Box sx={{ mb: 2, px: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Mức độ khó: {formData.difficulty}
                </Typography>
                <Slider
                  value={formData.difficulty || 5}
                  onChange={(_, value) => handleInputChange('difficulty', value)}
                  min={0}
                  max={10}
                  step={0.1}
                  marks={[
                    { value: 0, label: '0 (Dễ)' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10 (Khó)' }
                  ]}
                  valueLabelDisplay="auto"
                  sx={{
                    '& .MuiSlider-markLabel': {
                      '&:nth-of-type(1)': {
                        transform: 'translateX(12px)'
                      },
                      '&:nth-of-type(3)': {
                        transform: 'translateX(-12px)'
                      }
                    }
                  }}
                />
              </Box>

              {/* Tags */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Thẻ
                </Typography>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={1}>
                  {formData.tags?.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => removeTag(tag)}
                      size="small"
                    />
                  ))}
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    size="small"
                    label="Thêm thẻ"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button onClick={addTag} size="small">
                    Thêm
                  </Button>
                </Box>
              </Box>
            </>
          )}

          {/* Preview Tab */}
          {activeTab === 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Xem trước
              </Typography>
              
              {/* Question Text */}
              <Typography variant="body1" gutterBottom>
                <ContentRenderer text={formData.text || 'Không có nội dung câu hỏi'} />
              </Typography>
              
              {/* Options */}
              {formData.type === 'multiple_choice' && formData.options && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Các lựa chọn:
                  </Typography>
                  {formData.options.map((option, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                      {String.fromCharCode(65 + index)}. <ContentRenderer text={option || 'Không có nội dung lựa chọn'} />
                      {formData.correct_answer === (index + 1) && ' ✓'}
                    </Typography>
                  ))}
                </Box>
              )}
              
              {/* Explanation */}
              {formData.explanation && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Giải thích:
                  </Typography>
                  <Typography variant="body2">
                    <ContentRenderer text={formData.explanation} />
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Hủy
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Đang cập nhật...' : 'Cập nhật câu hỏi'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default QuestionEditForm; 